import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  BarChart3, Download, ArrowLeft, CheckCircle, AlertTriangle, 
  XCircle, Shield, Cpu, FileText, Building2, 
  Home, Loader2, AlertCircle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import API_URL from '../lib/apiUrl';
import jsPDF from 'jspdf';
import confetti from 'canvas-confetti';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { AssessmentReportVisuals } from '../components/report/AssessmentReportVisuals';
import { AIRecommendationsPanel } from '../components/report/AIRecommendationsPanel';


// Scoring utility functions
const getScoreFromValue = (value) => {
  if (!value) return 0;
  const positiveValues = ['yes', 'high', 'process_defined', 'yearly', 'half_yearly'];
  const neutralValues = ['unaware', 'medium'];
  const negativeValues = ['no', 'low', 'not_defined'];
  
  if (positiveValues.includes(value)) return 3;
  if (neutralValues.includes(value)) return 2;
  if (negativeValues.includes(value)) return 1;
  return 0;
};

const getColorFromScore = (percentage) => {
  if (percentage >= 76) return 'green';
  if (percentage >= 51) return 'yellow';
  return 'red';
};

// New Readiness Levels as per ISO 27001 assessment
const getMaturityLevel = (percentage) => {
  if (percentage >= 76) return { level: 'Mature', description: 'Organization has robust security controls and practices' };
  if (percentage >= 51) return { level: 'Managed', description: 'Security processes are established and monitored' };
  if (percentage >= 26) return { level: 'Developing', description: 'Basic security measures are being implemented' };
  return { level: 'Initial', description: 'Security practices are ad-hoc or minimal' };
};

// Heatmap Cell Component
const HeatmapCell = ({ value, label }) => {
  const score = getScoreFromValue(value);
  const bgColor = !value ? 'bg-slate-300' : score === 3 ? 'bg-green-600' : score === 2 ? 'bg-amber-500' : 'bg-red-600';
  const textColor = !value || score === 2 ? 'text-black' : 'text-white';
  
  return (
    <div 
      className={`${bgColor} ${textColor} p-2 rounded text-xs font-medium text-center truncate`}
      title={label}
      data-testid={`heatmap-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      {value ? value.charAt(0).toUpperCase() + value.slice(1).replace('_', ' ') : '-'}
    </div>
  );
};

// Score Card Component with Weight Display
const ScoreCard = ({ title, icon: Icon, score, maxScore, percentage, weight, weightedContribution, color }) => {
  const barColor = color === 'green' ? 'bg-emerald-600' : color === 'yellow' ? 'bg-amber-500' : 'bg-red-600';
  const testId = title.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="rounded-md border border-slate-200 bg-white p-4" data-testid={`score-card-${testId}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-slate-700" />
          <h3 className="font-body text-sm font-semibold text-black">{title}</h3>
        </div>
        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full font-medium">
          {weight}% weight
        </span>
      </div>
      <div className="flex items-end gap-2 mb-2">
        <span className="text-3xl font-bold text-black" data-testid={`score-card-${testId}-value`}>{percentage}%</span>
        <span className="text-sm text-slate-500 mb-1">({score}/{maxScore})</span>
      </div>
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden mb-2">
        <motion.div
          className={`h-full ${barColor}`}
          initial={{ width: 0 }}
          whileInView={{ width: `${percentage}%` }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
        />
      </div>
      <div className="text-xs text-slate-500">
        Contributes <span className="font-semibold text-slate-700">{weightedContribution}%</span> to overall score
      </div>
    </motion.div>
  );
};

// Recommendation Card Component
const RecommendationCard = ({ priority, title, description, category }) => {
  const priorityStyles = {
    high: { bg: 'bg-red-50 border-red-200', icon: XCircle, iconColor: 'text-red-500', badge: 'bg-red-100 text-red-700' },
    medium: { bg: 'bg-yellow-50 border-yellow-200', icon: AlertTriangle, iconColor: 'text-yellow-500', badge: 'bg-yellow-100 text-yellow-700' },
    low: { bg: 'bg-blue-50 border-blue-200', icon: AlertCircle, iconColor: 'text-blue-500', badge: 'bg-blue-100 text-blue-700' }
  };
  
  const style = priorityStyles[priority] || priorityStyles.medium;
  const Icon = style.icon;
  
  return (
    <div className={`${style.bg} border rounded-lg p-4`}>
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 ${style.iconColor} mt-0.5 flex-shrink-0`} />
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-body text-sm font-semibold text-black">{title}</h4>
            <span className={`${style.badge} text-xs px-2 py-0.5 rounded-full font-medium`}>
              {priority.toUpperCase()}
            </span>
          </div>
          <p className="font-body text-sm text-slate-600">{description}</p>
          <span className="text-xs text-slate-500 mt-1 block">Category: {category}</span>
        </div>
      </div>
    </div>
  );
};

// Question mappings for each category (defined outside component to prevent re-renders)
const CORE_QUESTIONS = [
  { id: 'top_management_involvement', label: 'Top Management Involvement' },
  { id: 'defined_security_objectives', label: 'Security Objectives' },
  { id: 'risk_assessment_conducted', label: 'Risk Assessment' },
  { id: 'asset_inventory_maintained', label: 'Asset Inventory' },
  { id: 'hr_onboarding_security_checks', label: 'HR Security Checks' },
  { id: 'access_control_process', label: 'Access Control' },
  { id: 'password_policy', label: 'Password Policy' },
  { id: 'cryptography_standards', label: 'Cryptography' },
  { id: 'incident_reporting_process', label: 'Incident Reporting' },
  { id: 'business_continuity_documented', label: 'Business Continuity' },
  { id: 'supplier_assessment', label: 'Supplier Assessment' },
  { id: 'change_management', label: 'Change Management' },
  { id: 'acceptable_use_policy', label: 'Acceptable Use Policy' },
  { id: 'staff_training', label: 'Staff Training' },
  { id: 'security_maintenance_frequency', label: 'Security Maintenance' }
];

const TECHNICAL_QUESTIONS = [
  { id: 'mfa_implemented', label: 'MFA Implementation' },
  { id: 'antivirus_edr', label: 'Antivirus/EDR' },
  { id: 'firewall_maintained', label: 'Firewall' },
  { id: 'siem_logs_monitored', label: 'SIEM/Logs' },
  { id: 'backups_automated', label: 'Backups' },
  { id: 'encryption_enabled', label: 'Encryption' },
  { id: 'secure_configurations', label: 'Secure Configs' },
  { id: 'endpoint_hardening', label: 'Endpoint Hardening' },
  { id: 'patch_automation', label: 'Patch Automation' },
  { id: 'vulnerability_scanning', label: 'Vulnerability Scanning' }
];

const DOCUMENTATION_QUESTIONS = [
  { id: 'policy_documents', label: 'Policy Documents' },
  { id: 'sops_defined', label: 'SOPs' },
  { id: 'annual_review', label: 'Annual Review' },
  { id: 'risk_register', label: 'Risk Register' },
  { id: 'isms_scope', label: 'ISMS Scope' },
  { id: 'internal_audit', label: 'Internal Audit' },
  { id: 'training_records', label: 'Training Records' },
  { id: 'supplier_documentation', label: 'Supplier Docs' },
  { id: 'incident_playbooks', label: 'Incident Playbooks' },
  { id: 'management_review_minutes', label: 'Management Reviews' }
];

const ORGANIZATION_QUESTIONS = [
  { id: 'has_compliance_team', label: 'Compliance Team' },
  { id: 'isms_awareness', label: 'ISMS Awareness' },
  { id: 'has_dedicated_it_team', label: 'IT Team' }
];

const AssessmentSummary = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [assessmentData, setAssessmentData] = useState(null);
  const [scores, setScores] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [statusCounts, setStatusCounts] = useState({ good: 0, attention: 0, critical: 0, unanswered: 0 });
  const [proAssessmentData, setProAssessmentData] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [reportSummary, setReportSummary] = useState(null);
  const [reportMeta, setReportMeta] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [aiRecommendationReport, setAiRecommendationReport] = useState(null);
  const [aiRecommendationsLoading, setAiRecommendationsLoading] = useState(false);
  const [aiRecommendationsError, setAiRecommendationsError] = useState('');

  const loadAIRecommendations = useCallback(async (retry = false) => {
    setAiRecommendationsLoading(true);
    setAiRecommendationsError('');
    try {
      const deadline = Date.now() + 180000;
      let forceRetry = retry === true;
      while (Date.now() < deadline) {
        const response = await axios.get(`${API_URL}/api/pro-assessment/${orgId}/ai-recommendations`, {
          headers: { Authorization: `Bearer ${token}` },
          params: forceRetry ? { retry: true } : undefined
        });
        forceRetry = false;
        if (response.status === 202 || response.data?.status === 'generating') {
          const pollDelay = (response.data?.poll_after_seconds || 3) * 1000;
          await new Promise(resolve => setTimeout(resolve, pollDelay));
          continue;
        }
        setAiRecommendationReport(response.data);
        setRecommendations(response.data.recommendations || []);
        return;
      }
      throw new Error('AI recommendation generation is taking longer than expected.');
    } catch (error) {
      console.error('Error loading AI recommendations:', error);
      const message = error.response?.data?.detail || 'AI recommendations could not be loaded. Please retry.';
      setAiRecommendationsError(message);
      setAiRecommendationReport(null);
      setRecommendations([]);
    } finally {
      setAiRecommendationsLoading(false);
    }
  }, [orgId, token]);

  // Generate recommendations based on assessment data
  const generateRecommendations = useCallback((data) => {
    const recs = [];
    
    // Core Assessment Recommendations
    if (data.core) {
      if (data.core.top_management_involvement === 'low') {
        recs.push({
          priority: 'high',
          title: 'Increase Top Management Involvement',
          description: 'Senior leadership engagement is critical for ISMS success. Schedule regular security briefings and include security metrics in board reports.',
          category: 'Core Assessment'
        });
      }
      if (data.core.risk_assessment_conducted === 'no' || data.core.risk_assessment_conducted === 'unaware') {
        recs.push({
          priority: 'high',
          title: 'Conduct Risk Assessment',
          description: 'Perform a comprehensive risk assessment to identify, analyze, and evaluate information security risks.',
          category: 'Core Assessment'
        });
      }
      if (data.core.incident_reporting_process === 'not_defined' || data.core.incident_reporting_process === 'unaware') {
        recs.push({
          priority: 'high',
          title: 'Define Incident Reporting Process',
          description: 'Establish clear incident reporting procedures to ensure timely detection and response to security events.',
          category: 'Core Assessment'
        });
      }
      if (data.core.staff_training === 'no' || data.core.staff_training === 'unaware') {
        recs.push({
          priority: 'medium',
          title: 'Implement Security Awareness Training',
          description: 'Regular staff training on security policies and procedures is essential for maintaining a security-conscious culture.',
          category: 'Core Assessment'
        });
      }
      if (data.core.business_continuity_documented === 'no') {
        recs.push({
          priority: 'high',
          title: 'Document Business Continuity Plan',
          description: 'Create and maintain a business continuity plan to ensure organizational resilience during disruptions.',
          category: 'Core Assessment'
        });
      }
    }

    // Technical Controls Recommendations
    if (data.technical) {
      if (data.technical.mfa_implemented === 'no') {
        recs.push({
          priority: 'high',
          title: 'Implement Multi-Factor Authentication',
          description: 'MFA is a critical security control. Implement it for all user accounts, especially for privileged access.',
          category: 'Technical Controls'
        });
      }
      if (data.technical.encryption_enabled === 'no') {
        recs.push({
          priority: 'high',
          title: 'Enable Data Encryption',
          description: 'Encrypt sensitive data at rest and in transit to protect against unauthorized access.',
          category: 'Technical Controls'
        });
      }
      if (data.technical.vulnerability_scanning === 'no' || data.technical.vulnerability_scanning === 'unaware') {
        recs.push({
          priority: 'medium',
          title: 'Establish Vulnerability Management',
          description: 'Implement regular vulnerability scanning and patch management processes.',
          category: 'Technical Controls'
        });
      }
      if (data.technical.siem_logs_monitored === 'no') {
        recs.push({
          priority: 'medium',
          title: 'Implement Security Monitoring',
          description: 'Deploy SIEM solution or centralized logging to detect and respond to security incidents.',
          category: 'Technical Controls'
        });
      }
      if (data.technical.backups_automated === 'no') {
        recs.push({
          priority: 'medium',
          title: 'Automate Backup Processes',
          description: 'Implement automated backup solutions to ensure data recovery capabilities.',
          category: 'Technical Controls'
        });
      }
    }

    // Documentation Recommendations
    if (data.documentation) {
      if (data.documentation.policy_documents === 'no') {
        recs.push({
          priority: 'high',
          title: 'Develop Security Policies',
          description: 'Create comprehensive security policies covering information security, acceptable use, and data protection.',
          category: 'Documentation'
        });
      }
      if (data.documentation.risk_register === 'no') {
        recs.push({
          priority: 'high',
          title: 'Create Risk Register',
          description: 'Maintain a risk register to track identified risks, their treatments, and residual risk levels.',
          category: 'Documentation'
        });
      }
      if (data.documentation.internal_audit === 'no') {
        recs.push({
          priority: 'medium',
          title: 'Schedule Internal Audits',
          description: 'Conduct regular internal audits to verify compliance with ISMS requirements.',
          category: 'Documentation'
        });
      }
      if (data.documentation.incident_playbooks === 'no') {
        recs.push({
          priority: 'medium',
          title: 'Create Incident Response Playbooks',
          description: 'Develop playbooks for common security incidents to ensure consistent and effective response.',
          category: 'Documentation'
        });
      }
    }

    // Organization Basics Recommendations
    if (data.organization) {
      if (data.organization.has_compliance_team === 'no') {
        recs.push({
          priority: 'medium',
          title: 'Establish Security/Compliance Team',
          description: 'Designate personnel responsible for information security management and compliance.',
          category: 'Organization'
        });
      }
      if (data.organization.isms_awareness === 'no') {
        recs.push({
          priority: 'low',
          title: 'ISMS Awareness Program',
          description: 'Educate leadership and staff about Information Security Management Systems and their benefits.',
          category: 'Organization'
        });
      }
    }

    // Sort by priority
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    recs.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

    return recs;
  }, []);

  // Fetch a server-calculated, ownership-scoped report.
  useEffect(() => {
    const fetchAssessmentData = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const [summaryRes, subRes] = await Promise.all([
          axios.get(`${API_URL}/api/assessment-summary/${orgId}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/api/subscription/status`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        const summary = summaryRes.data;
        setSubscription(subRes.data);
        setProAssessmentData(summary.pro_assessment);
        setReportSummary(summary.report_summary);
        setReportMeta(summary.report_meta);
        const data = {
          organization: summary.organization,
          core: summary.core_assessment,
          technical: summary.technical_controls,
          documentation: summary.documentation_readiness
        };
        setAssessmentData(data);
        setScores(summary.scores);
        setRecommendations(subRes.data?.plan === 'pro' ? [] : generateRecommendations(data));
        setStatusCounts(summary.status_counts);

        if (summary.report_summary.completion_rate === 100) setTimeout(() => {
          confetti({
            particleCount: 80,
            spread: 65,
            origin: { y: 0.6 }
          });
        }, 500);
      } catch (error) {
        console.error('Error fetching assessment data:', error);
        const message = error.response?.data?.detail || 'This assessment report could not be loaded.';
        setLoadError(message);
        toast.error(message);
      } finally {
        setLoading(false);
      }
    };

    fetchAssessmentData();
  }, [orgId, token, generateRecommendations]);

  useEffect(() => {
    if (subscription?.plan === 'pro' && proAssessmentData?.scores?.totalAnswered > 0) {
      loadAIRecommendations();
    }
  }, [subscription?.plan, proAssessmentData?.scores?.totalAnswered, loadAIRecommendations]);

  const handleBack = () => {
    // For Pro users who completed Controls Assessment, go back to Controls Assessment
    if (subscription?.plan === 'pro') {
      navigate(`/controls-assessment/${orgId}`);
    } else {
      navigate(`/documentation-readiness/${orgId}`);
    }
  };

  const handleDashboard = () => {
    navigate('/dashboard');
  };

  const [isExporting, setIsExporting] = useState(false);
  const reportRef = useRef(null);

  const handleExportPDF = async () => {
    if (!reportRef.current) {
      toast.error('Report content not found');
      return;
    }

    setIsExporting(true);
    toast.info('Generating PDF... Please wait');

    try {
      const orgName = assessmentData?.organization?.org_name || 'Organization';
      const date = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;

      // Helper function to add new page if needed
      const checkPageBreak = (requiredHeight) => {
        if (yPos + requiredHeight > pageHeight - margin) {
          pdf.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };

      // Title Page
      pdf.setFillColor(30, 41, 59); // slate-800
      pdf.rect(0, 0, pageWidth, 60, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('ISO 27001:2022', margin, 25);
      pdf.setFontSize(18);
      pdf.text('Maturity Assessment Report', margin, 35);
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(orgName, margin, 48);
      pdf.text(`Generated: ${date}`, pageWidth - margin - 50, 48);
      pdf.setFontSize(8);
      pdf.text(`Report ID: ${reportMeta?.report_id || 'Unavailable'}`, margin, 56);

      yPos = 75;

      // Overall Score Section
      pdf.setTextColor(30, 41, 59);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Client Maturity Score', margin, yPos);
      yPos += 10;

      const overallPercentage = reportSummary?.maturity_score || 0;
      const maturityLevel = {
        level: reportSummary?.maturity_level || getMaturityLevel(overallPercentage).level,
        description: reportSummary?.maturity_description || getMaturityLevel(overallPercentage).description
      };
      
      // Score box
      pdf.setFillColor(overallPercentage >= 76 ? 34 : overallPercentage >= 51 ? 234 : 239, 
                       overallPercentage >= 76 ? 197 : overallPercentage >= 51 ? 179 : 68,
                       overallPercentage >= 76 ? 94 : overallPercentage >= 51 ? 8 : 68);
      pdf.roundedRect(margin, yPos, 50, 25, 3, 3, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${overallPercentage}%`, margin + 25, yPos + 16, { align: 'center' });

      pdf.setTextColor(30, 41, 59);
      pdf.setFontSize(14);
      pdf.text(`Maturity Level: ${maturityLevel.level}`, margin + 60, yPos + 10);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(maturityLevel.description, margin + 60, yPos + 18);
      pdf.setFontSize(8);
      pdf.text(`${reportSummary?.answered_count || 0} of ${reportSummary?.total_questions || 0} persisted responses used`, margin + 60, yPos + 24);

      yPos += 35;

      // Readiness Scale Legend
      pdf.setFontSize(9);
      pdf.setTextColor(100, 116, 139);
      pdf.text('Readiness Scale: 0-25% Initial | 26-50% Developing | 51-75% Managed | 76-100% Mature', margin, yPos);
      yPos += 15;

      // Category Scores
      pdf.setTextColor(30, 41, 59);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Category Breakdown (Weighted)', margin, yPos);
      yPos += 8;

      const categories = [
        { name: 'Organization Basics', weight: '10%', score: scores?.organization },
        { name: 'Core ISO Assessment', weight: '45%', score: scores?.core },
        { name: 'Technical Controls', weight: '25%', score: scores?.technical },
        { name: 'Documentation Readiness', weight: '20%', score: scores?.documentation }
      ];

      categories.forEach(cat => {
        const percentage = cat.score?.percentage || 0;
        const color = percentage >= 76 ? [34, 197, 94] : percentage >= 51 ? [234, 179, 8] : [239, 68, 68];
        
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(30, 41, 59);
        pdf.text(`${cat.name} (${cat.weight})`, margin, yPos + 4);
        pdf.text(`${percentage}%`, pageWidth - margin - 15, yPos + 4);
        
        // Progress bar background
        pdf.setFillColor(226, 232, 240);
        pdf.roundedRect(margin + 70, yPos, 80, 6, 1, 1, 'F');
        
        // Progress bar fill
        pdf.setFillColor(...color);
        pdf.roundedRect(margin + 70, yPos, (percentage / 100) * 80, 6, 1, 1, 'F');
        
        yPos += 12;
      });

      yPos += 10;

      // Status Distribution
      checkPageBreak(40);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 41, 59);
      pdf.text('Status Distribution', margin, yPos);
      yPos += 10;

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      
      // Good
      pdf.setFillColor(34, 197, 94);
      pdf.circle(margin + 3, yPos, 3, 'F');
      pdf.text(`Good: ${statusCounts.good} controls`, margin + 10, yPos + 1);
      
      // Attention
      pdf.setFillColor(234, 179, 8);
      pdf.circle(margin + 60, yPos, 3, 'F');
      pdf.text(`Needs Attention: ${statusCounts.attention} controls`, margin + 67, yPos + 1);
      
      // Critical
      pdf.setFillColor(239, 68, 68);
      pdf.circle(margin + 140, yPos, 3, 'F');
      pdf.text(`Critical: ${statusCounts.critical} controls`, margin + 147, yPos + 1);

      yPos += 20;

      // Detailed Assessment Responses
      const addQuestionSection = (title, questions, data, icon) => {
        checkPageBreak(30);
        
        pdf.setFillColor(241, 245, 249);
        pdf.rect(margin - 5, yPos - 5, pageWidth - 2 * margin + 10, 12, 'F');
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 41, 59);
        pdf.text(title, margin, yPos + 3);
        yPos += 15;

        questions.forEach((q, index) => {
          checkPageBreak(12);
          
          const answer = data?.[q.id] || 'Not answered';
          const score = getScoreFromValue(answer);
          const color = score === 3 ? [34, 197, 94] : score === 2 ? [234, 179, 8] : [239, 68, 68];
          
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(71, 85, 105);
          pdf.text(`${index + 1}. ${q.label}`, margin, yPos);
          
          // Answer badge
          pdf.setFillColor(...color);
          const answerText = answer.charAt(0).toUpperCase() + answer.slice(1).replace('_', ' ');
          const textWidth = pdf.getTextWidth(answerText);
          pdf.roundedRect(pageWidth - margin - textWidth - 8, yPos - 4, textWidth + 6, 6, 1, 1, 'F');
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(8);
          pdf.text(answerText, pageWidth - margin - textWidth - 5, yPos - 0.5);
          
          yPos += 8;
        });
        
        yPos += 8;
      };

      // Add all question sections
      addQuestionSection('Organization Basics', ORGANIZATION_QUESTIONS, assessmentData?.organization);
      addQuestionSection('Core ISO Assessment', CORE_QUESTIONS, assessmentData?.core);
      addQuestionSection('Technical Controls', TECHNICAL_QUESTIONS, assessmentData?.technical);
      addQuestionSection('Documentation Readiness', DOCUMENTATION_QUESTIONS, assessmentData?.documentation);

      // Recommendations Section
      if (recommendations.length > 0) {
        checkPageBreak(30);
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 41, 59);
        pdf.text('Recommendations', margin, yPos);
        yPos += 10;

        recommendations.slice(0, 10).forEach((rec, index) => {
          checkPageBreak(18);
          
          const priorityColor = rec.priority === 'critical' ? [185, 28, 28] :
                               rec.priority === 'high' ? [239, 68, 68] :
                               rec.priority === 'medium' ? [234, 179, 8] : [59, 130, 246];
          
          pdf.setFillColor(...priorityColor);
          pdf.circle(margin + 2, yPos + 2, 2, 'F');
          
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(30, 41, 59);
          pdf.text(rec.title, margin + 8, yPos + 3);
          
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(71, 85, 105);
          pdf.setFontSize(8);
          
          // Word wrap description
          const maxWidth = pageWidth - 2 * margin - 8;
          const recommendationText = rec.description || rec.finding || rec.rationale || '';
          const lines = pdf.splitTextToSize(recommendationText, maxWidth);
          pdf.text(lines.slice(0, 2), margin + 8, yPos + 9);
          
          yPos += 8 + (Math.min(lines.length, 2) * 4);
        });

        const recommendationDisclaimer = 'Recommendations are based on questionnaire responses and may differ from real-world conditions. Independent professional validation is advised.';
        const disclaimerLines = pdf.splitTextToSize(recommendationDisclaimer, pageWidth - (2 * margin) - 35);
        checkPageBreak((disclaimerLines.length * 3) + 12);
        yPos += 5;
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin + 35, yPos, pageWidth - margin, yPos);
        yPos += 5;
        pdf.setFontSize(6.5);
        pdf.setFont('helvetica', 'italic');
        pdf.setTextColor(148, 163, 184);
        pdf.text(disclaimerLines, pageWidth - margin, yPos, { align: 'right' });
        yPos += disclaimerLines.length * 3;
      }

      // Footer on last page
      pdf.setFontSize(8);
      pdf.setTextColor(148, 163, 184);
      pdf.text(`ISO 27001:2022 Maturity Assessment - ${orgName} - Page ${pdf.internal.getNumberOfPages()}`, 
               pageWidth / 2, pageHeight - 10, { align: 'center' });

      // Save the PDF
      pdf.save(`ISO27001_Assessment_${orgName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
      
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-slate-900 mx-auto mb-4" />
          <p className="font-body text-lg text-slate-600">Loading assessment summary...</p>
        </div>
      </div>
    );
  }

  if (loadError || !assessmentData || !scores || !reportSummary) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4" data-testid="assessment-report-error-state">
        <div className="w-full max-w-lg rounded-md border border-red-200 bg-white p-8 text-center">
          <AlertCircle className="mx-auto h-10 w-10 text-red-500" />
          <h1 className="mt-5 text-3xl font-semibold text-slate-900">Report unavailable</h1>
          <p className="mt-3 text-sm leading-6 text-slate-600" data-testid="assessment-report-error-message">{loadError || 'No persisted responses were found for this assessment.'}</p>
          <Button onClick={handleDashboard} className="mt-6" data-testid="assessment-report-error-dashboard-button"><Home className="mr-2 h-4 w-4" />Return to dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 py-8 sm:py-12" data-testid="assessment-summary-page">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="mb-8 flex flex-col justify-between gap-5 sm:flex-row sm:items-start" data-testid="assessment-report-header">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="h-8 w-8 text-slate-900" />
                <h1 className="font-heading text-4xl font-bold text-black" data-testid="assessment-report-title">
                  Assessment Summary
                </h1>
              </div>
              <p className="font-body text-lg text-slate-700">
                {assessmentData?.organization?.org_name || 'Organization'} - ISO 27001:2022 Maturity Assessment
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleExportPDF} disabled={isExporting} data-testid="assessment-report-export-button">
                {isExporting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Export PDF
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Report Content - ref for PDF export */}
          <div ref={reportRef}>
          <div className="mb-6 flex flex-col justify-between gap-4 rounded-md border border-slate-300 bg-white px-5 py-4 sm:flex-row sm:items-center" data-testid="assessment-report-provenance">
            <div><p className="text-xs uppercase tracking-[0.18em] text-slate-500">Client-specific report</p><p className="mt-1 font-semibold text-slate-900" data-testid="assessment-report-id">{reportMeta?.report_id}</p></div>
            <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-xs text-slate-600 sm:text-right"><span>Organization</span><strong className="text-slate-900" data-testid="assessment-report-organization">{reportMeta?.organization_name}</strong><span>Calculation</span><strong className="text-slate-900" data-testid="assessment-report-calculation-method">Persisted answers</strong></div>
          </div>

          <div className="mb-8">
            <AssessmentReportVisuals reportSummary={reportSummary} scores={scores} statusCounts={statusCounts} proScores={proAssessmentData?.scores} />
          </div>

          {/* Category Scores */}
          <div className="mb-8">
            <h2 className="font-heading font-semibold text-xl text-black mb-4">Category Distribution (Weighted)</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <ScoreCard 
                title="Organization Basics" 
                icon={Building2} 
                score={scores?.organization.score || 0} 
                maxScore={scores?.organization.max || 9}
                percentage={scores?.organization.percentage || 0}
                weight={scores?.organization.weight || 10}
                weightedContribution={scores?.organization.weightedContribution || 0}
                color={getColorFromScore(scores?.organization.percentage || 0)}
              />
              <ScoreCard 
                title="Core Assessment" 
                icon={Shield} 
                score={scores?.core.score || 0} 
                maxScore={scores?.core.max || 45}
                percentage={scores?.core.percentage || 0}
                weight={scores?.core.weight || 45}
                weightedContribution={scores?.core.weightedContribution || 0}
                color={getColorFromScore(scores?.core.percentage || 0)}
              />
              <ScoreCard 
                title="Technical Controls" 
                icon={Cpu} 
                score={scores?.technical.score || 0} 
                maxScore={scores?.technical.max || 30}
                percentage={scores?.technical.percentage || 0}
                weight={scores?.technical.weight || 25}
                weightedContribution={scores?.technical.weightedContribution || 0}
                color={getColorFromScore(scores?.technical.percentage || 0)}
              />
              <ScoreCard 
                title="Documentation" 
                icon={FileText} 
                score={scores?.documentation.score || 0} 
                maxScore={scores?.documentation.max || 30}
                percentage={scores?.documentation.percentage || 0}
                weight={scores?.documentation.weight || 20}
                weightedContribution={scores?.documentation.weightedContribution || 0}
                color={getColorFromScore(scores?.documentation.percentage || 0)}
              />
            </div>
          </div>

          {/* Pro Assessment - Controls Assessment ISO 27001:2022 (Only for Pro users) */}
          {subscription?.plan === 'pro' && proAssessmentData?.scores && (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl shadow-lg p-6 mb-8 border border-amber-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-amber-500 rounded-lg">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-xl text-amber-900">
                    Controls Assessment - ISO 27001:2022
                  </h2>
                  <p className="text-sm text-amber-700">93 Annex A Controls Assessment Results</p>
                </div>
                <span className="ml-auto px-3 py-1 bg-amber-500 text-white text-xs font-bold rounded-full">PRO</span>
              </div>
              
              {/* Controls Assessment Scores by Category */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {proAssessmentData.scores.categoryScores && Object.entries(proAssessmentData.scores.categoryScores).map(([category, data]) => {
                  const categoryNames = {
                    organizational: { name: 'Organizational', count: 37, icon: Building2 },
                    people: { name: 'People', count: 8, icon: Shield },
                    physical: { name: 'Physical', count: 14, icon: Cpu },
                    technological: { name: 'Technological', count: 34, icon: FileText }
                  };
                  const catInfo = categoryNames[category] || { name: category, count: 0, icon: Shield };
                  const CatIcon = catInfo.icon;
                  const percentage = data?.percentage || 0;
                  
                  return (
                    <div key={category} className="bg-white rounded-xl p-4 border border-amber-200">
                      <div className="flex items-center gap-2 mb-2">
                        <CatIcon className="h-4 w-4 text-amber-600" />
                        <span className="font-semibold text-sm text-slate-800">{catInfo.name}</span>
                        <span className="text-xs text-slate-500 ml-auto">{catInfo.count} controls</span>
                      </div>
                      <div className="flex items-end gap-2">
                        <span className={`text-2xl font-bold ${
                          percentage >= 76 ? 'text-green-600' : 
                          percentage >= 51 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {Math.round(percentage)}%
                        </span>
                        <span className="text-xs text-slate-500 mb-1">maturity</span>
                      </div>
                      <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all ${
                            percentage >= 76 ? 'bg-green-500' : 
                            percentage >= 51 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Overall Controls Assessment Score */}
              {proAssessmentData.scores.overallPercentage !== undefined && (
                <div className="bg-white rounded-xl p-6 border border-amber-200">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-1">Overall Controls Maturity</h3>
                      <p className="text-sm text-slate-500">Based on all 93 ISO 27001:2022 Annex A Controls</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <div className={`text-4xl font-bold ${
                          proAssessmentData.scores.overallPercentage >= 76 ? 'text-green-600' : 
                          proAssessmentData.scores.overallPercentage >= 51 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {Math.round(proAssessmentData.scores.overallPercentage || 0)}%
                        </div>
                        <div className="text-xs text-slate-500">Overall Score</div>
                      </div>
                      <div className={`px-4 py-2 rounded-lg text-sm font-semibold ${
                        proAssessmentData.scores.overallPercentage >= 76 ? 'bg-green-100 text-green-700' : 
                        proAssessmentData.scores.overallPercentage >= 51 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {proAssessmentData.scores.maturityLevel?.name || (
                          proAssessmentData.scores.overallPercentage >= 76 ? 'Mature' : 
                          proAssessmentData.scores.overallPercentage >= 51 ? 'Managed' : 
                          proAssessmentData.scores.overallPercentage >= 26 ? 'Developing' : 'Initial'
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Heatmap */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-slate-200">
            <h2 className="font-heading font-semibold text-xl text-black mb-4">Assessment Heatmap</h2>
            <div className="flex items-center gap-4 mb-4 text-sm">
              <span className="flex items-center gap-1">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span className="text-slate-600">Good</span>
              </span>
              <span className="flex items-center gap-1">
                <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                <span className="text-slate-600">Needs Attention</span>
              </span>
              <span className="flex items-center gap-1">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span className="text-slate-600">Critical</span>
              </span>
            </div>
            
            <div className="space-y-6">
              {/* Core Assessment Heatmap */}
              <div>
                <h3 className="font-body text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <Shield className="h-4 w-4" /> Core Assessment
                </h3>
                <div className="grid grid-cols-5 md:grid-cols-8 lg:grid-cols-15 gap-1">
                  {CORE_QUESTIONS.map(q => (
                    <HeatmapCell 
                      key={q.id} 
                      value={assessmentData?.core?.[q.id]} 
                      label={q.label}
                    />
                  ))}
                </div>
              </div>

              {/* Technical Controls Heatmap */}
              <div>
                <h3 className="font-body text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <Cpu className="h-4 w-4" /> Technical Controls
                </h3>
                <div className="grid grid-cols-5 md:grid-cols-10 gap-1">
                  {TECHNICAL_QUESTIONS.map(q => (
                    <HeatmapCell 
                      key={q.id} 
                      value={assessmentData?.technical?.[q.id]} 
                      label={q.label}
                    />
                  ))}
                </div>
              </div>

              {/* Documentation Heatmap */}
              <div>
                <h3 className="font-body text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4" /> Documentation Readiness
                </h3>
                <div className="grid grid-cols-5 md:grid-cols-10 gap-1">
                  {DOCUMENTATION_QUESTIONS.map(q => (
                    <HeatmapCell 
                      key={q.id} 
                      value={assessmentData?.documentation?.[q.id]} 
                      label={q.label}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {subscription?.plan === 'pro' && proAssessmentData?.scores?.totalAnswered > 0 ? (
            <AIRecommendationsPanel
              loading={aiRecommendationsLoading}
              error={aiRecommendationsError}
              report={aiRecommendationReport}
              onRetry={() => loadAIRecommendations(true)}
            />
          ) : (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-slate-200" data-testid="rules-recommendations-panel">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-semibold text-xl text-black">Recommendations</h2>
              <span className="text-sm text-slate-500">{recommendations.length} items</span>
            </div>
            
            {recommendations.length > 0 ? (
              <div className="space-y-3">
                {recommendations.map((rec, index) => (
                  <RecommendationCard key={index} {...rec} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                <p className="text-lg font-medium text-black">Excellent!</p>
                <p className="text-slate-600">No critical recommendations at this time.</p>
              </div>
            )}
          </div>
          )}
          </div>
          {/* End of reportRef */}

          {/* Navigation Buttons */}
          <div className="flex gap-4">
            <Button
              variant="outline"
              onClick={handleBack}
              className="flex-1 py-6 text-base font-semibold"
              data-testid="back-button"
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              Previous Page
            </Button>
            <Button
              onClick={handleDashboard}
              className="flex-1 py-6 text-base font-semibold"
              data-testid="dashboard-button"
            >
              <Home className="mr-2 h-5 w-5" />
              Go to Dashboard
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AssessmentSummary;
